import { Button as AntButton, Descriptions, Modal } from "antd";
import { FC, useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { useTranslation } from 'react-i18next';
import { approveProfolio, getPortfolio } from "../../../../api/tutor.api";
import useAxios from "../../../../hooks/useAxios";
import useNotification from "../../../../hooks/useNotification";
import { IGetPortfolio, IPortfolioFile, IPortfolioItem } from "../../../../models/tutor.model";
import Radio from "../../../Radio";
import useLoader from "../../../../hooks/useLoader";
import useUtil from "../../../../hooks/useUtil";
import TextViewer from "./TextViewer";
import _ from "lodash";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  item: IPortfolioItem|undefined;
  doGetPortfolios: () => void;
}

const ShowPortfolioModal: FC<IProps> = ({
  visible,
  setVisible,
  item,
  doGetPortfolios,
}) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const { getDetailCodes, getCommonCodeLabel } = useUtil();
  const { data: res, isLoading, error, execute } = useAxios<IGetPortfolio>();
  const { data: postRes, error: postError, execute: postExecute } = useAxios();
  useLoader(
    isLoading,
    [error, postError],
  );

  const [fileInfo, setFileInfo] = useState<IPortfolioFile[]>([]);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [memo, setMemo] = useState('');
  // GR0004 = 포트폴리오 강사승인 상태
  const GR0004 = getDetailCodes('GR0004');

  useEffect(() => {
    if (item) {
      execute(getPortfolio(item.classCode, item.resultCode));
    }
  }, [item]);

  useEffect(() => {
    if (res?.data) {
      let newFileInfo = _.cloneDeep(res.data.fileInfo || []);
      const txPortfolio = newFileInfo.find(item => item.portfolioResultTypeCode === 'TX');
      if (txPortfolio) {
        newFileInfo = _.orderBy(newFileInfo, ['portfolioResultTypeCode'], ['desc']);
      }
      setFileInfo(newFileInfo);
      setApprovalStatus(res.data.approvalStatus);
      setMemo(res.data.memo);
    }
  }, [res]);

  useEffect(() => {
    if (postRes) {
      doGetPortfolios();
      successNotification(t('common.message.save1'));
      onClose();
    }
  }, [postRes]);

  const onClose = () => {
    setApprovalStatus('');
    setMemo('');
    setVisible(false);
  }

  const renderContent = (item: IPortfolioFile, canbotContentFileUrl?: string) => {
    switch (item.portfolioResultTypeCode) {
      case 'IM':
        return (
          <img
            src={item.file?.fileLocation}
            alt=""
            style={{ maxWidth: '100%' }}
          />
        );
      case 'SD':
        return (
          <audio
            controls
            src={item.file?.fileLocation}
          />
        );
      case 'MP':
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ReactPlayer
              url={item.file?.fileLocation}
              width="50%"
              height="auto"
              controls
            />
          </div>
        );
      case 'TX':
        return (
          <TextViewer
            title={item.textFileInfo?.title}
            body={item.textFileInfo?.body}
          />
        );
      case 'CB':
        return (
          <>
            <div style={{display:'flex', justifyContent:'center'}}  >
              <audio
                controls
                src={canbotContentFileUrl}
              />
            </div>
            <br/>
            <img
              src={item.file?.fileLocation}
              alt=""
              style={{ maxWidth: '100%' }}
            />
          </>
        );        
      default:
        return null;
    }
  }

  const renderContentCanbotSummary = (activityType: string) => {
    if(activityType === 'AC') {
      return (
        <div className="content-item">
          {res?.data?.canbotSummaryAudioAttachFileCode && (
            <>
              <div style={{display:'flex', justifyContent:'center'}}  >
                <audio
                  controls
                  src={res?.data?.canbotSummaryAudioFile?.fileLocation}
                />
              </div>
              <br/>
            </>
          )}
          {res?.data?.canbotSummaryImageAttachFileCode && (
            <>
              <img
                src={res?.data?.canbotSummaryImageFile?.fileLocation}
                alt=""
                style={{ maxWidth: '100%' }}
              />
              <br/>
              <br/>
            </>
          )}
        </div>
      );
    } else {
      return null;
    }
  }

  const onSubmit = () => {
    if (!item) return;

    if (!approvalStatus) {
      return infoNotification(t('class.portfolio.message1'));
    }

    postExecute(approveProfolio({
      resultCode: item.resultCode,
      approvalStatus,
      memo,
    }));
  }

  return (
    <Modal
      title={t('class.portfolio.title2')}
      visible={visible}
      width={750}
      onCancel={onClose}
      footer={[
        <div className='comm-btn__area justify-content-center'>	
          <AntButton
            key="submit"
            className='btn-basic pink'
            onClick={onSubmit}
          >
            {t('common.button.save')}
          </AntButton>          
          <AntButton
            key="cancel"
            className='btn-basic gray'
            onClick={onClose}
          >
            {t('common.button.close')}
          </AntButton>
        </div>
      ]}
    >
      <div className='comm-table__area rowType mt-0'>
        <Descriptions
          column={2}
          size="small"
          bordered
          labelStyle={{ width: '15%' }}
          contentStyle={{ width: '35%' }}
        >
          <Descriptions.Item label={t('common.label.1')}>
            {getCommonCodeLabel('PR0006', res?.data?.eduStepCode)}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.column82')}>
            {res?.data?.studentKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.menu.class')}>
            {res?.data?.className}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.portfolio.column1')}>
            {getCommonCodeLabel('GR0001', res?.data?.learningTypeCode)}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.28')}>
            {item?.curriculumName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.portfolio.column3')}>
            {res?.data?.activityName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.portfolio.column9')}>
            {res?.data?.firstApprovalMemberKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.portfolio.column10')}>
            {res?.data?.lastApprovalMemberKorName}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="my-4">
        {renderContentCanbotSummary(res?.data?.activityType || '')}
        {fileInfo.map((item, index) => (
          <div
            key={index}
            className="content-item"
          >
            {renderContent(item, res?.data?.canbotContentFileUrl)}              
          </div>
        ))}
      </div>

      <div className='comm-table__area rowType mt-0'>
        <Descriptions
          bordered
          column={1}
          size="small"
          labelStyle={{ width: '30%' }}
        >
          <Descriptions.Item label={t('class.portfolio.column6')}>
            {GR0004?.map((item: any, index: number) => (
              <Radio
                className={index === 0 ? '' : 'ml-4'}
                checked={item.detailCode === approvalStatus}
                onChange={() => setApprovalStatus(item.detailCode)}
                text={item.korName}
              />
            ))}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.portfolio.column11')}>
            <input
              type="text"
              className="form-control"
              value={memo}
              onChange={e => setMemo(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter') onSubmit() }}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  );
};

export default ShowPortfolioModal;